import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import styled from 'styled-components';
import 'isomorphic-fetch';

import { Button } from '@codex-brightworld/basics';
import { Rollup } from '@codex-brightworld/gadgets';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';

import DynamicBundles from './DynamicBundles/DynamicBundles';
import { fetchStaticBundles, fetchStaticBundleInfo } from './helpers';
import { dispatchSetProductBundlesListForStaticBundles } from './productPageBundlesAnalytics';
import StaticBundle from './StaticBundles/StaticBundle';


const BUNDLES_API_FALLBACK = 'https://api.currys.co.uk/store/api';
const BUNDLES_API_PATH = '/products/[PRODUCT_ID]/visitors/[VISITOR_ID]/bundles';

const ButtonWrapper = styled.div`
   text-align: center
`;

const StyledButton = styled(Button)`
   @media ${(props) => props.theme.breakpoints.desktop} {
       width: 230px;
   }
   
   @media ${(props) => props.theme.breakpoints.tablet} {
       width: 230px;
   }
`;


const initProductPageBundles = (psaVariant) => {
  const bundlesPanelEl = document.querySelector('#bundles-panel');
  const mainProductId = window.mainProductId;
  if (!bundlesPanelEl || !mainProductId) {
    // no bundle is allowed for this product
    return;
  }

  window.addEventListener('load', async () => {
    let staticBundlesData = [];
    let dynamicBundlesComponent = null;

    // 1. gathering data for dynamic bundles
    const {
      dynamicBundlesCreatingPossible, packBuilderAjaxUrl,
      accessoriesCatsJSON, dynamicBundleMax, dynamicBundleLabel, dynamicBundlePrice,
      dynamicBundleImageUrl
    } = window;

    if (dynamicBundlesCreatingPossible) {
      dynamicBundlesComponent = (
        <DynamicBundles
          key={'dynamicBundles'}
          mainProductId={mainProductId}
          updateDynamicBundle={
            async (action, productId = null) =>
              fetchStaticBundleInfo(packBuilderAjaxUrl, action, productId)
          }
          accessoriesCategories={accessoriesCatsJSON}
          dynamicBundleMax={dynamicBundleMax}
          dynamicBundleLabel={dynamicBundleLabel}
          dynamicBundlePrice={dynamicBundlePrice}
          dynamicBundleImageUrl={dynamicBundleImageUrl}
        />
      );
    }

    // 2. gathering data for static bundles
    if (window.App.settings.get('component.sca.active')) {
      const visitorId = window.adobeVisID || 0;
      const storeApiUrl = window.App.settings.get('bundles.api') || BUNDLES_API_FALLBACK;
      const bundlesApiPath = BUNDLES_API_PATH
        .replace('[PRODUCT_ID]', mainProductId)
        .replace('[VISITOR_ID]', visitorId);

      const staticBundlesResponse = await fetchStaticBundles(storeApiUrl, bundlesApiPath);

      if (staticBundlesResponse.ok) {
        staticBundlesData = staticBundlesResponse.bundlesData;

        // analytics
        if (staticBundlesData.length > 0) {
          dispatchSetProductBundlesListForStaticBundles(staticBundlesData);
        }

      } else {
        console.error("It wasn't possible to gather static bundles due to following reason:");
        console.error(staticBundlesResponse.error.message);
      }
    }

    if (staticBundlesData.length === 0 && dynamicBundlesComponent === null) {
      // no bundles at all -> nothing to render
      return;
    }

    if (staticBundlesData.length === 1 && dynamicBundlesComponent === null) {
      // Only one Static Bundle - no rolup

      ReactDOM.render(
        <Provider store={window.appState}>
          <EnvironmentThemeProvider>
            <StaticBundle
              psaVariant={psaVariant}
              {...staticBundlesData[0]}
            />
          </EnvironmentThemeProvider>
        </Provider>, bundlesPanelEl
      );
    } else if (staticBundlesData.length === 0 && dynamicBundlesComponent !== null) {
      // Only Dynamic Bundle - no rolup

      ReactDOM.render(
        <Provider store={window.appState}>
          <EnvironmentThemeProvider>
            {dynamicBundlesComponent}
          </EnvironmentThemeProvider>
        </Provider>, bundlesPanelEl
      );
    } else {
      // Rollup: One Static Bundle + more static bundles + dynamic bundles

      const bundlesComponentsForRollup = [
        <StaticBundle
          key={`${staticBundlesData[0].id}-firstStaticBundle`}
          psaVariant={psaVariant}
          {...staticBundlesData[0]}
        />
      ];

      if (staticBundlesData.length > 1) {
        bundlesComponentsForRollup.push(
          <>
            {staticBundlesData.slice(1).map((bundle, index) => (
              <StaticBundle
                key={`${bundle.id}-${index}`}
                psaVariant={psaVariant}
                {...bundle}
              />
            ))}
          </>
        );
      }

      if (dynamicBundlesComponent !== null) {
        bundlesComponentsForRollup.push(dynamicBundlesComponent);
      }

      ReactDOM.render(
        <Provider store={window.appState}>
          <EnvironmentThemeProvider>
            <Rollup
              items={bundlesComponentsForRollup}
              step={bundlesComponentsForRollup.length - 1}
              initialCount={1}
            >
              {({
                items, totalItems, toDisplay, toHide, displayMore, displayLess
              }) => (
                <>
                  {items.map((item, index) => (
                    <Fragment key={`${index}-rollup-item`}>
                      {item}
                    </Fragment>
                  ))}
                  {toDisplay > 0 && (
                    <ButtonWrapper>
                      <StyledButton
                        onClick={(e) => {
                          displayMore(e);
                        }}
                      >
                        Show more bundles
                      </StyledButton>

                    </ButtonWrapper>
                  )}
                  {toHide > 0 && toDisplay === 0 && (
                    <ButtonWrapper>
                      <StyledButton
                        onClick={(e) => {
                          displayLess(e);
                          document.querySelector('#bundles-panel').scrollIntoView();
                        }}
                      >
                        Show less bundles
                      </StyledButton>
                    </ButtonWrapper>
                  )}
                </>
              )}
            </Rollup>
          </EnvironmentThemeProvider>
        </Provider>, bundlesPanelEl
      );
    }
  });
};

export default initProductPageBundles;
